import React, { useReducer, useState, useRef, useContext } from 'react';
import { MultistepApiContext, MultistepStateContext, FormContext } from '../Context';

const initialState = {
  current: null,
  steps: new Set(),
  relevanceMap: new Map(),
};

const getNext = ({ current, steps, relevanceMap }) => {
  let nextCurrent = current == null ? 0 : current + 1;

  while (relevanceMap.get([...steps][nextCurrent]) === false && nextCurrent < steps.size) {
    nextCurrent += 1;
  }

  return nextCurrent < steps.size ? nextCurrent : null;
};

const getPrevious = ({ current, steps, relevanceMap }) => {
  let nextCurrent = current == null ? 0 : current - 1;

  while (relevanceMap.get([...steps][nextCurrent]) === false && nextCurrent >= 0) {
    nextCurrent -= 1;
  }

  return nextCurrent >= 0 ? nextCurrent : null;
};

const getCurrent = ({ current, steps }) => {
  return [...steps][current];
};

function multistepReducer(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case 'next': {
      const { current } = state;
      const nextCurrent = getNext(state);

      return {
        ...state,
        current: nextCurrent != null ? nextCurrent : current,
      };
    }
    case 'previous': {
      const { current } = state;
      const nextCurrent = getPrevious(state);

      return {
        ...state,
        current: nextCurrent != null ? nextCurrent : current,
      };
    }
    case 'register': {
      const { id, isRelevant } = payload;
      const { current, relevanceMap, steps } = state;

      return {
        ...state,
        current: current ?? 0,
        steps: new Set([...steps, id]),
        relevanceMap: new Map([...relevanceMap, [id, isRelevant]]),
      };
    }
    case 'setCurrent': {
      const { steps } = state;
      const { id: nextId } = payload;
      const nextCurrent = [...steps].indexOf(nextId);

      if (nextCurrent > -1) {
        return {
          ...state,
          current: nextCurrent,
        };
      }

      return state;
    }
    case 'setRelevance': {
      const { relevanceMap } = state;
      const { id, isRelevant } = payload;

      return {
        ...state,
        relevanceMap: new Map([...relevanceMap, [id, isRelevant]]),
      };
    }
    case 'unregister': {
      const { current, steps } = state;

      const updatedSteps = new Set([...steps]);
      updatedSteps.delete(payload);

      const isCurrent = payload === getCurrent(state);
      const updatedCurrent = isCurrent ? getPrevious(state) : current;

      return {
        ...state,
        current: updatedCurrent >= 0 ? updatedCurrent : null,
        steps: updatedSteps,
      };
    }
    default:
      throw new Error();
  }
}

export default function useMultistep() {
  const stateRef = useRef();
  const context = useContext(FormContext);
  const [multistepState, dispatch] = useReducer(multistepReducer, initialState);

  stateRef.current = multistepState;

  const [multistepApi] = useState({
    getCurrent() {
      return getCurrent(stateRef.current);
    },
    getCurrentIndex() {
      const { steps } = stateRef.current;

      return [...steps].indexOf(getCurrent(stateRef.current));
    },
    getIndex(id) {
      const { steps } = stateRef.current;

      return [...steps].indexOf(id);
    },
    getRelevance(id) {
      const { relevanceMap } = stateRef.current;

      return relevanceMap.get(id);
    },
    getSteps() {
      return [...stateRef.current.steps];
    },
    async next() {
      context.setCurrentTouched();
      const validationResult = await context.validate();

      if (validationResult === false) {
        context.focusFirstError();
      } else {
        dispatch({ type: 'next' });
      }
    },
    previous() {
      dispatch({ type: 'previous' });
    },
    setCurrent(id) {
      dispatch({ type: 'setCurrent', payload: { id } });
    },
    register(id, isRelevant) {
      dispatch({ type: 'register', payload: { id, isRelevant } });
    },
    setRelevance(id, isRelevant) {
      dispatch({ type: 'setRelevance', payload: { id, isRelevant } });
    },
    unregister(payload) {
      dispatch({ type: 'unregister', payload });
    },
  });

  const render = (children) => (
    <MultistepApiContext.Provider value={multistepApi}>
      <MultistepStateContext.Provider
        value={{
          ...multistepState,
          hasNext: getNext(multistepState) != null,
          hasPrevious: getPrevious(multistepState) != null,
          currentId: getCurrent(multistepState),
        }}
      >
        {children}
      </MultistepStateContext.Provider>
    </MultistepApiContext.Provider>
  );

  return {
    ...multistepApi,
    ...multistepState,
    hasNext: getNext(multistepState) != null,
    hasPrevious: getPrevious(multistepState) != null,
    render,
  };
}
